import { ErrorMessage } from "@atlaskit/form";
import { currencyFormatter } from "@fla/common";
import {
  AssetType,
  Button,
  ConsentOrdersPathName,
  FormNavigation,
  InvestmentsBaseProps,
  InvestmentsBaseSchema,
  Investments_InitialState,
  RadioSelect,
  useInvestments,
  useNames
} from "@fla/components";
import InvestmentsIcon from "@fla/components/src/assets/svgs/investment.svg";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "app/store";
import { withConsentOrder } from "components/HOCs/withConsentOrder";
import { useConsentOrders } from "hooks/ConsentOrders";
import { SliceStatus } from "interfaces";
import { selectAsset, selectConsentOrdersBase } from "pages/ConsentOrders";
import { consentOrdersActions } from "pages/ConsentOrders/consentOrdersSlice";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

const defaultValues = {
  if_own_investment: "no"
};

function Investments() {
  const { initialDetails } = useSelector(selectConsentOrdersBase);
  const investmentsData = useSelector(selectAsset(AssetType.INVESTMENTS)) as Investments_InitialState[];
  const _useNames = useNames({ initialDetails });
  const { spouseFirstName } = _useNames;
  const [status, setStatus] = useState(SliceStatus.Idle);
  const [error, setError] = useState<string>();

  const location = useLocation();
  const navigate = useNavigate();
  const { handleSubmit, watch, control, reset, getValues } = useForm({
    mode: "all",
    defaultValues,
    resolver: yupResolver(InvestmentsBaseSchema)
  });

  const handleBackClick = () => {
    navigate(ConsentOrdersPathName.Vehicle);
  };

  const editData = (values?: InvestmentsBaseProps) => {
    navigate(`${ConsentOrdersPathName.Investments}/add`, {
      state: {
        parent: getValues(),
        child: values
      }
    });
  };

  const dispatch = useAppDispatch();
  const { updateAssetsDebts } = useConsentOrders();

  const onSubmit = async (values: InvestmentsBaseProps) => {
    try {
      setStatus(SliceStatus.Pending);

      if (values.if_own_investment === "no" && investmentsData.length) {
        setError(
          "If neither of you have any investments in Australia, then please remove the added investments details."
        );
      } else if (values.if_own_investment === "yes" && !investmentsData.length) {
        setError("please add investment details.");
      } else {
        setError("");

        const payload = {
          investments: { data: values, isCompleted: true },
          currentStep: 6
        };
        await updateAssetsDebts(payload);
        dispatch(consentOrdersActions.setAssetsDebts(payload));
        navigate(ConsentOrdersPathName.Business);
      }
      setStatus(SliceStatus.Resolved);
    } catch (error) {
      setStatus(SliceStatus.Rejected);
    }
  };

  useEffect(() => {
    if (location.state) {
      reset(location.state);
    } else {
      reset(defaultValues);
    }
  }, [location]);

  const { ownCard, splitCard, userTotalAmount, spouseTotalAmount } = useInvestments({
    investmentsData,
    ..._useNames
  });

  return (
    <section className="w-full py-10">
      <form
        id="investment-form"
        className="md:w-10/12 lg:max-w-2xl mx-auto h-fit px-5 md:px-10 md:py-5 space-y-5"
        onSubmit={handleSubmit(onSubmit)}
      >
        <img src={InvestmentsIcon} className="w-10 h-auto mx-auto my-3" alt="investment" />

        <h3 className="text-center my-5">Investments</h3>

        <RadioSelect
          title={`Do you or ${spouseFirstName} own an have any investments in Australia?`}
          name="if_own_investment"
          id="if_own_investment"
          control={control}
          options={[
            { label: "Yes", value: "yes" },
            { label: "No", value: "no" }
          ]}
          type="card"
          onChange={() => setError("")}
          theme="dark"
        />

        <div className="w-full py-10 border border-gray-400 border-dashed rounded-lg flex flex-col items-center justify-center space-y-10 mb-5">
          <p className="px-16 text-center text-base font-bold">
            Please add all the investments you and {spouseFirstName} have in Australia
          </p>
          <Button
            className="px-5 py-2 uppercase"
            fontSize="sm"
            onClick={() => editData(undefined)}
            disabled={watch("if_own_investment") !== "yes"}
          >
            + Add Investments
          </Button>
        </div>

        {error ? (
          <div className="max-w-sm mx-auto text-center">
            <ErrorMessage>{error}</ErrorMessage>
          </div>
        ) : null}

        {Array.isArray(investmentsData) && investmentsData.length ? (
          <>
            <p className="uppercase text-gray-500 text-xl font-medium my-5">You’re Keeping</p>

            {ownCard("user", editData)}
            {splitCard("user", editData)}
            <section className="flex justify-end items-center mb-12">
              <p className="text-gray-600">
                Total:
                <span className="text-2xl ml-2">{currencyFormatter.format(userTotalAmount)}</span>
              </p>
            </section>

            <p className="uppercase text-gray-500 text-xl font-medium my-5">{spouseFirstName} Keeping</p>

            {ownCard("spouse", editData)}
            {splitCard("spouse", editData)}
            <section className="flex justify-end items-center mb-12">
              <p className="text-gray-600">
                Total:
                <span className="text-2xl ml-2">{currencyFormatter.format(spouseTotalAmount)}</span>
              </p>
            </section>
          </>
        ) : null}

        <FormNavigation
          isFirst={false}
          isLast={false}
          form="investment-form"
          handleBackClick={handleBackClick}
          nextButtonText="Continue"
          isSubmitting={status === SliceStatus.Pending}
          classes="mt-12"
        />
      </form>
    </section>
  );
}
export default withConsentOrder(Investments);
